import { locales } from "senf-atomic-design-system/src/components/molecules/languageSettings/LanguageSettings";
import i18n from "./i18n";
import { cleanAndParseAiJson } from "./reinsertionHelpers";

type GeminiRequestProps = {
  body: string;
  assistantId: string;
  lang: string;
  image?: {
    data: string;
    mimeType: string;
  };
  images?: Array<{
    data: string;
    mimeType: string;
  }>;
  signal?: AbortSignal;
};

export const geminiRequest = async ({
  body,
  assistantId,
  lang,
  image,
  images,
  signal,
}: GeminiRequestProps) => {
  try {
    console.log(`Sending request to Gemini with assistantId: ${assistantId}`);
    console.log(
      `Body length: ${body.length}, Has image: ${!!image}, Has images: ${!!images?.length}`,
    );

    // Convert single image to images array if provided
    const finalImages = images || (image ? [image] : undefined);

    const response = await fetch(
      `${import.meta.env.VITE_FIREBASE_CLOUD_FUNCTIONS_URL}/gemini`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          body: body,
          assistantId: assistantId,
          lang:
            i18n.t(
              `languages:${locales[lang].languageKey}.${locales[lang].languageKey}`,
            ) || "DE",
          images: finalImages,
        }),
        signal,
      },
    );

    console.log(`Response status: ${response.status} ${response.statusText}`);
    console.log(
      `Response headers: ${JSON.stringify([...response.headers.entries()])}`,
    );

    if (!response.ok) {
      console.error(
        `Error with the request: ${response.status} ${response.statusText}`,
      );

      // Try to read the error message
      try {
        const errorText = await response.text();
        console.error(`Error response: ${errorText}`);
      } catch (readError) {
        console.error(`Failed to read error response: ${readError}`);
      }

      return null;
    }

    // Check the Content-Type to determine how to handle the response
    const contentType = response.headers.get("Content-Type");
    console.log(`Response Content-Type: ${contentType}`);

    // Direct handling of image binary data
    if (
      contentType &&
      (contentType.includes("image") || contentType.includes("octet-stream"))
    ) {
      console.log("Processing binary image response...");
      try {
        // Get the binary data for direct return
        // The client code will handle creation of the image from this data
        const arrayBuffer = await response.arrayBuffer();
        console.log(
          `Received binary data, size: ${arrayBuffer.byteLength} bytes`,
        );

        if (arrayBuffer.byteLength === 0) {
          console.error("Received empty array buffer");
          return null;
        }

        // Convert ArrayBuffer to base64
        const base64 = arrayBufferToBase64(arrayBuffer);
        console.log(`Converted to base64, length: ${base64.length}`);
        return base64;
      } catch (binaryError) {
        console.error(`Error processing binary response: ${binaryError}`);
        return null;
      }
    }
    // Try to detect if response is binary data despite wrong Content-Type
    else {
      try {
        // First, get the raw response as an ArrayBuffer to check if it's binary
        const clonedResponse = response.clone();
        const arrayBuffer = await clonedResponse.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);

        // Check for PNG/JPEG signatures or other binary patterns
        const isProbablyBinary =
          // PNG signature
          (uint8Array[0] === 0x89 &&
            uint8Array[1] === 0x50 &&
            uint8Array[2] === 0x4e &&
            uint8Array[3] === 0x47) ||
          // JPEG signature
          (uint8Array[0] === 0xff && uint8Array[1] === 0xd8) ||
          // Check if it contains many non-printable characters
          uint8Array
            .slice(0, 100)
            .filter((b) => b < 32 && b !== 9 && b !== 10 && b !== 13).length >
            10;

        if (isProbablyBinary) {
          console.log("Detected binary data with incorrect Content-Type");
          const base64 = arrayBufferToBase64(arrayBuffer);
          console.log(
            `Converted binary data to base64, length: ${base64.length}`,
          );
          return base64;
        }

        // If it's not binary, get it as text
        const data = await response.text();
        console.log(
          `Received text response, length: ${data?.length || 0} chars`,
        );

        // Clean and parse JSON if it's a JSON response
        const cleanedData = cleanAndParseAiJson(data);
        return cleanedData;
      } catch (error) {
        console.error(`Error processing response: ${error}`);

        // Fall back to text
        try {
          const textResponse = await response.text();
          const cleanedData = cleanAndParseAiJson(textResponse);
          return cleanedData;
        } catch (textError) {
          console.error(`Error reading as text: ${textError}`);
          return null;
        }
      }
    }
  } catch (error) {
    console.error(`Error with the request: ${error}`);

    if (error instanceof TypeError && error.message.includes("NetworkError")) {
      console.error(
        "Network error detected. This might be a CORS or connection issue.",
      );
    }

    return null;
  }
};

// Convert ArrayBuffer to base64
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}
